import React, { FC } from "react";

const pulse =
  "animate-pulse rounded-md bg-slate-200 dark:bg-slate-600";

/** Menu hero + status row + category / filter chips (matches HeaderFilterSection layout). */
export const MenuPageHeaderSkeleton: FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`flex flex-col relative mb-12 ${className}`}
    data-testid="isShowLoading"
  >
    <div
      className={`relative w-full max-h-[240px] min-h-[120px] h-[28vh] rounded-lg ${pulse}`}
      aria-hidden
    />
    <div className="flex gap-2 mt-4">
      <div className={`h-9 w-28 rounded-lg ${pulse}`} aria-hidden />
      <div className={`h-9 w-20 rounded-lg ${pulse}`} aria-hidden />
    </div>
    <div className="flex flex-wrap gap-2 mt-4 w-full">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={`h-10 w-24 rounded-full ${pulse}`} aria-hidden />
      ))}
    </div>
    <div className="w-full border-b border-slate-200 dark:border-slate-600 mt-4" />
    <div className="flex flex-wrap gap-2 mt-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`h-9 w-20 rounded-lg ${pulse}`} aria-hidden />
      ))}
    </div>
  </div>
);

/** Product grid placeholders (same breakpoints as PageHomeMenu). */
export const MenuProductGridSkeleton: FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`grid gap-4 sm:gap-8 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 ${className}`}
    aria-busy
    aria-label="Loading menu items"
  >
    {Array.from({ length: 10 }).map((_, i) => (
      <div key={i} className="flex flex-col nc-ProductCard relative">
        <div className={`aspect-square w-full rounded-2xl ${pulse}`} aria-hidden />
        <div className={`mt-4 h-5 w-[85%] ${pulse}`} aria-hidden />
        <div className={`mt-2 h-4 w-1/2 ${pulse}`} aria-hidden />
        <div className={`mt-3 h-6 w-20 rounded-full ${pulse}`} aria-hidden />
      </div>
    ))}
  </div>
);

/** Product detail: image + title / options / CTA. */
export const ProductDetailPageSkeleton: FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`nc-ProductDetailPage ${className}`}
    aria-busy
    aria-label="Loading product"
  >
    <main className="container mt-5 mb-5 lg:mt-11">
      <div className="lg:flex gap-8">
        <div className="w-full lg:w-[55%]">
          <div className={`aspect-square w-full rounded-lg ${pulse}`} aria-hidden />
        </div>
        <div className="w-full lg:w-[45%] pt-10 lg:pt-0 lg:pl-7 space-y-4">
          <div className={`h-8 w-4/5 ${pulse}`} aria-hidden />
          <div className={`h-5 w-1/3 ${pulse}`} aria-hidden />
          <div className={`h-24 w-full rounded-lg ${pulse}`} aria-hidden />
          <div className={`h-12 w-full max-w-xs rounded-full ${pulse}`} aria-hidden />
        </div>
      </div>
    </main>
  </div>
);

/** Kanopy / hosted payment iframe area before script loads. */
export const PaymentFormSkeleton: FC<{ className?: string }> = ({
  className = "",
}) => (
  <div
    className={`rounded-xl border border-slate-200 dark:border-slate-600 p-4 space-y-3 ${className}`}
    aria-busy
    aria-label="Loading payment form"
  >
    <div className={`h-4 w-2/5 ${pulse}`} />
    <div className={`h-10 w-full ${pulse}`} />
    <div className={`h-10 w-full ${pulse}`} />
    <div className="grid grid-cols-2 gap-3">
      <div className={`h-10 w-full ${pulse}`} />
      <div className={`h-10 w-full ${pulse}`} />
    </div>
  </div>
);
